import { GameState } from "@/GameState";
import { EngineMode } from "@/enums/engine/EngineMode";
import { GameMenu, LBL_MapView } from "@/gui";
import type { GUILabel, GUIButton, GUICheckBox, GUIProgressBar } from "@/gui";
import { TextureLoader } from "@/loaders";
import { OdysseyTexture } from "@/three/odyssey/OdysseyTexture";
import * as THREE from "three";
import { Anchor } from "@/enums/gui/Anchor";
import { SSFType } from "@/enums/resource/SSFType";
import { TalentObject } from "@/talents";
import { EngineState } from "@/enums/engine/EngineState";
import { AutoPauseState } from "@/enums/engine/AutoPauseState";
import { BitWise } from "@/utility/BitWise";
import { KeyMapAction, ModuleObjectType } from "@/enums";
import type { ModuleObject } from "@/module/ModuleObject";

const TLK_TOOLTIP_FULL_HEALTH = 42498;

const TLK_TOOLTIP_SOLOMODE = 48035;
const TLK_TOOLTIP_STEALTH = 247;
const TLK_TOOLTIP_PAUSE = 48019;

const TLK_TOOLTIP_QUEST     = 48218;
const TLK_TOOLTIP_EQUIPMENT = 48219;
const TLK_TOOLTIP_INVENTORY = 48220;
const TLK_TOOLTIP_MAP       = 48221;
const TLK_TOOLTIP_OPTIONS   = 48222;
const TLK_TOOLTIP_MESSAGES  = 48223;
const TLK_TOOLTIP_ABILITIES = 48224;
const TLK_TOOLTIP_CHARACTER = 48225;

const TLK_TOOLTIP_FRIENDLY_POWER = 48486;
const TLK_TOOLTIP_FRIENDLY_ITEM = 48291;
const TLK_TOOLTIP_FRIENDLY_NON_MEDICAL_ITEM = 48299;
const TLK_TOOLTIP_FRIENDLY_MINE = 48295;

const preloadTextures = ['enemy_bar', 'hostilearrow', 'friend_bar', 'friendlyarrow'];
const preloadTexturesMap = new Map<string, OdysseyTexture>();

const ARROW_SCALE = 32;
const ARROW_SCALE_HALF = ARROW_SCALE / 2;
const ARROW_DIR_LEFT = 0;
const ARROW_DIR_DOWN = Math.PI/2;
const ARROW_DIR_RIGHT = Math.PI;

/**
 * InGameOverlay class.
 * 
 * KotOR JS - A remake of the Odyssey Game Engine that powered KotOR I & II
 * 
 * @file InGameOverlay.ts
 * @author KobaltBlu <https://github.com/KobaltBlu>
 * @license {@link https://www.gnu.org/licenses/gpl-3.0.txt|GPLv3}
 */
export class InGameOverlay extends GameMenu {

  engineMode: EngineMode = EngineMode.INGAME;
  LBL_CMBTMODEMSG: GUILabel;
  LBL_COMBATBG2: GUILabel;
  LBL_COMBATBG3: GUILabel;
  LBL_COMBATBG1: GUILabel;
  LBL_MAP: GUILabel;
  LBL_CMBTMSGBG: GUILabel;
  LBL_MOULDING1: GUILabel;
  LBL_ACTIONDESCBG: GUILabel;
  LBL_ACTIONDESC: GUILabel;
  LBL_MOULDING2: GUILabel;
  TB_STEALTH: GUICheckBox;
  LBL_ARROW_MARGIN: GUILabel;
  TB_SOLO: GUICheckBox;
  LBL_MAPBORDER: GUILabel;
  LBL_MAPVIEW: GUILabel;
  BTN_MINIMAP: GUIButton;
  LBL_MOULDING3: GUILabel;
  LBL_MENUBG: GUILabel;
  TB_PAUSE: GUICheckBox;
  LBL_NAMEBG: GUILabel;
  LBL_HEALTHBG: GUILabel;
  BTN_MSG: GUIButton;
  BTN_JOU: GUIButton;
  BTN_MAP: GUIButton;
  BTN_OPT: GUIButton;
  BTN_CHAR: GUIButton;
  BTN_ABI: GUIButton;
  BTN_INV: GUIButton;
  BTN_EQU: GUIButton;
  LBL_BACK2: GUILabel;
  LBL_BACK3: GUILabel;
  LBL_CHAR3: GUILabel;
  LBL_DISABLE3: GUILabel;
  LBL_DEBILATATED3: GUILabel;
  LBL_LVLUPBG3: GUILabel;
  LBL_LEVELUP3: GUILabel;
  LBL_STEALTHXP: GUILabel;
  BTN_ACTION0: GUIButton;
  BTN_ACTIONUP0: GUIButton;
  BTN_ACTIONDOWN0: GUIButton;
  LBL_ACTION0: GUIButton;
  BTN_ACTION3: GUIButton;
  BTN_ACTIONUP3: GUIButton;
  BTN_ACTIONDOWN3: GUIButton;
  LBL_ACTION3: GUIButton;
  BTN_ACTION2: GUIButton;
  BTN_ACTIONUP2: GUIButton;
  BTN_ACTIONDOWN2: GUIButton;
  LBL_ACTION2: GUIButton;
  BTN_ACTION1: GUIButton;
  BTN_ACTIONUP1: GUIButton;
  BTN_ACTIONDOWN1: GUIButton;
  LBL_ACTION1: GUIButton;
  LBL_DARKSHIFT: GUILabel;
  LBL_BACK1: GUILabel;
  PB_FORCE2: GUIProgressBar;
  PB_VIT2: GUIProgressBar;
  PB_VIT1: GUIProgressBar;
  LBL_CHAR1: GUILabel;
  LBL_DISABLE1: GUILabel;
  LBL_DEBILATATED1: GUILabel;
  LBL_LVLUPBG1: GUILabel;
  LBL_LEVELUP1: GUILabel;
  LBL_CHAR2: GUILabel;
  LBL_DEBILATATED2: GUILabel;
  LBL_DISABLE2: GUILabel;
  PB_FORCE1: GUIProgressBar;
  PB_FORCE3: GUIProgressBar;
  PB_VIT3: GUIProgressBar;
  LBL_NAME: GUILabel;
  LBL_LVLUPBG2: GUILabel;
  PB_HEALTH: GUIProgressBar;
  LBL_LEVELUP2: GUILabel;
  LBL_CMBTEFCTRED1: GUILabel;
  LBL_CMBTEFCTINC1: GUILabel;
  LBL_CMBTEFCTRED2: GUILabel;
  LBL_CMBTEFCTINC2: GUILabel;
  LBL_CMBTEFCTINC3: GUILabel;
  LBL_CMBTEFCTRED3: GUILabel;
  LBL_ARROW: GUILabel;
  LBL_JOURNAL: GUILabel;
  LBL_CASH: GUILabel;
  LBL_PLOTXP: GUILabel;
  LBL_ITEMRCVD: GUILabel;
  LBL_ITEMLOST: GUILabel;
  BTN_CLEARALL: GUIButton;
  LBL_LIGHTSHIFT: GUILabel;
  BTN_CHAR3: GUIButton;
  BTN_CHAR1: GUIButton;
  BTN_CHAR2: GUIButton;
  LBL_QUEUE0: GUILabel;
  LBL_QUEUE1: GUILabel;
  LBL_QUEUE2: GUILabel;
  LBL_QUEUE3: GUILabel;
  BTN_CLEARONE: GUIButton;
  BTN_CLEARONE2: GUIButton;
  BTN_TARGET0: GUIButton;
  BTN_TARGETUP0: GUIButton;
  BTN_TARGETDOWN0: GUIButton;
  LBL_TARGET0: GUIButton;
  BTN_TARGET1: GUIButton;
  BTN_TARGETUP1: GUIButton;
  BTN_TARGETDOWN1: GUIButton;
  LBL_TARGET1: GUIButton;
  BTN_TARGET2: GUIButton;
  BTN_TARGETUP2: GUIButton;
  BTN_TARGETDOWN2: GUIButton;
  LBL_TARGET2: GUIButton;
  miniMap: LBL_MapView;

  namePlateArrow: THREE.Mesh;
  namePlateArrowMaterial: THREE.MeshBasicMaterial;

  constructor(){
    super();
    this.gui_resref = 'mipc28x6';
    this.background = '';
    this.voidFill = false;
    this.enablePositionScaling = true;
    this.userCanClose = false;
  }

  async menuControlInitializer(skipInit: boolean = false) {
    await super.menuControlInitializer();
    if(skipInit) return;
    return new Promise<void>(async (resolve, reject) => {
      this.tGuiPanel.widget.userData.fill.visible = false;
      //this.TB_STEALTH.hideBorder();
      //this.TB_PAUSE.hideBorder();
      //this.TB_SOLO.hideBorder();
      this.LBL_MENUBG.setDepthMode(2);

      this.LBL_LIGHTSHIFT?.hide();
      this.LBL_DARKSHIFT?.hide();
      this.LBL_JOURNAL?.hide();
      this.LBL_CASH?.hide();
      this.LBL_PLOTXP?.hide();
      this.LBL_STEALTHXP?.hide();
      this.LBL_ITEMRCVD?.hide();
      this.LBL_ITEMLOST?.hide();

      //Map INIT
      //this.LBL_MAPBORDER.hideBorder();
      this.LBL_MAP?.hide();
      this.LBL_ARROW_MARGIN?.hide();
      this.LBL_ARROW?.hide();
      this.miniMap = new LBL_MapView(this.LBL_MAPVIEW);
      this.miniMap.setControl(this.LBL_MAPVIEW);
      this.miniMap.setSize(120, 120);

      this.LBL_CMBTEFCTRED1?.hide();
      this.LBL_CMBTEFCTINC1?.hide();
      this.LBL_LEVELUP1?.hide();
      this.LBL_LVLUPBG1?.hide();
      this.LBL_DEBILATATED1?.hide();
      this.LBL_DISABLE1?.hide();

      this.LBL_CMBTEFCTRED2?.hide();
      this.LBL_CMBTEFCTINC2?.hide();
      this.LBL_LEVELUP2?.hide();
      this.LBL_LVLUPBG2?.hide();
      this.LBL_DEBILATATED2?.hide();
      this.LBL_DISABLE2?.hide();

      this.LBL_CMBTEFCTRED3?.hide();
      this.LBL_CMBTEFCTINC3?.hide();
      this.LBL_LEVELUP3?.hide();
      this.LBL_LVLUPBG3?.hide();
      this.LBL_DEBILATATED3?.hide();
      this.LBL_DISABLE3?.hide();


      this.LBL_ACTIONDESC?.hide();
      this.LBL_ACTIONDESCBG?.hide();

      this.LBL_NAME?.hide();
      this.LBL_NAMEBG?.hide();
      this.PB_HEALTH?.hide();
      this.LBL_HEALTHBG?.hide();

      this.LBL_CMBTMSGBG?.hide();
      this.LBL_CMBTMODEMSG?.hide();
      //this.BTN_CLEARALL.hideBorder();


      this.BTN_MSG.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuMessages.open();
      }).setKeymapAction(KeyMapAction.Messages).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_MESSAGES].Value
      );

      this.BTN_JOU.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuJournal.open();
      }).setKeymapAction(KeyMapAction.Quests).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_QUEST].Value
      );

      this.BTN_MAP.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuMap.open();
      }).setKeymapAction(KeyMapAction.Map).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_MAP].Value
      );

      this.BTN_OPT.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuOptions.open();
      }).setKeymapAction(KeyMapAction.Options).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_OPTIONS].Value
      );

      this.BTN_CHAR.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuCharacter.open();
      }).setKeymapAction(KeyMapAction.Character).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_CHARACTER].Value
      );

      this.BTN_ABI.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuAbilities.open();
      }).setKeymapAction(KeyMapAction.SkillsAndFeats).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_ABILITIES].Value
      );

      this.BTN_INV.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuInventory.open();
      }).setKeymapAction(KeyMapAction.Inventory).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_INVENTORY].Value
      );

      this.BTN_EQU.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuEquipment.open();
      }).setKeymapAction(KeyMapAction.Equip).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_EQUIPMENT].Value
      );

      this.TB_PAUSE.addEventListener('click', (e) => {
        e.stopPropagation();

        if(GameState.State == EngineState.PAUSED){
          GameState.AutoPauseManager.Unpause();
        }else{
          GameState.AutoPauseManager.SignalAutoPauseEvent(AutoPauseState.Generic);
        }

      }).setKeymapAction(KeyMapAction.Pause).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_PAUSE].Value
      );

      this.TB_SOLO.addEventListener('click', (e) => {
        e.stopPropagation();
        GameState.SOLOMODE = !GameState.SOLOMODE;
      }).setKeymapAction(KeyMapAction.PartyActive).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_SOLOMODE].Value
      );

      this.TB_STEALTH.addEventListener('click', (e) => {
        e.stopPropagation();
      }).setKeymapAction(KeyMapAction.STEALTH).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_STEALTH].Value
      );

      this.BTN_CHAR1.addEventListener('click', (e) => {
        if(GameState.PartyManager.party[0].canLevelUp()){
          this.manager.MenuCharacter.open();
        }else{
          this.manager.MenuEquipment.open();
        }
      });

      this.BTN_CHAR2.addEventListener('click', (e) => {
        GameState.PartyManager.SwitchLeaderAtIndex(2);
        switch(Math.floor(Math.random() * (4 - 1) + 1)){
          case 2:
            GameState.PartyManager.party[0].playSoundSet(SSFType.SELECT_2);
          break;
          case 3:
            GameState.PartyManager.party[0].playSoundSet(SSFType.SELECT_3);
          break;
          default:
            GameState.PartyManager.party[0].playSoundSet(SSFType.SELECT_1);
          break;
        }
      });

      this.BTN_CHAR3.addEventListener('click', (e) => {
        GameState.PartyManager.SwitchLeaderAtIndex(1);
        switch(Math.floor(Math.random() * (4 - 1) + 1)){
          case 2:
            GameState.PartyManager.party[0].playSoundSet(SSFType.SELECT_2);
          break;
          case 3:
            GameState.PartyManager.party[0].playSoundSet(SSFType.SELECT_3);
          break;
          default:
            GameState.PartyManager.party[0].playSoundSet(SSFType.SELECT_1);
          break;
        }
      });

      this.BTN_CLEARALL.addEventListener('click', (e) => {
        e.stopPropagation();
        GameState.getCurrentPlayer().clearAllActions();
        GameState.getCurrentPlayer().combatData.combatState = false;
        GameState.getCurrentPlayer().cancelCombat();
      });

      this.LBL_QUEUE0.addEventListener('click', (e) => {
        e.stopPropagation();
        // GameState.getCurrentPlayer().clearCombatAction(GameState.getCurrentPlayer().combatData.combatAction);
      });

      this.LBL_QUEUE1.addEventListener('click', (e) => {
        e.stopPropagation();
        GameState.getCurrentPlayer().clearCombatActionAtIndex(0);
      });

      this.LBL_QUEUE2.addEventListener('click', (e) => {
        e.stopPropagation();
        GameState.getCurrentPlayer().clearCombatActionAtIndex(1);
      });

      this.LBL_QUEUE3.addEventListener('click', (e) => {
        e.stopPropagation();
        GameState.getCurrentPlayer().clearCombatActionAtIndex(2);
      });

      for(let i = 0; i < GameState.ActionMenuManager.TARGET_MENU_COUNT; i++){

        this.getControlByName('LBL_TARGET'+i).addEventListener('click', (e) => {
          e.stopPropagation();
          GameState.ActionMenuManager.onTargetMenuAction(i);
        });

        this.getControlByName('BTN_TARGETUP'+i).addEventListener('click', (e) => {
          e.stopPropagation();
          GameState.ActionMenuManager.ActionPanels.targetPanels[i].previousAction();
          this.UpdateTargetUIIcon(i);
        });

        this.getControlByName('BTN_TARGETDOWN'+i).addEventListener('click', (e) => {
          e.stopPropagation();
          GameState.ActionMenuManager.ActionPanels.targetPanels[i].nextAction();
          this.UpdateTargetUIIcon(i);
        });

      }

      for(let i = 0; i < GameState.ActionMenuManager.SELF_MENU_COUNT; i++){

        this.getControlByName('LBL_ACTION'+i).addEventListener('click', (e) => {
          e.stopPropagation();
          GameState.ActionMenuManager.onSelfMenuAction(i);
        });

        this.getControlByName('BTN_ACTIONUP'+i).addEventListener('click', (e) => {
          e.stopPropagation();
          GameState.ActionMenuManager.ActionPanels.selfPanels[i].previousAction();
          this.UpdateSelfUIIcon(i);
        });

        this.getControlByName('BTN_ACTIONDOWN'+i).addEventListener('click', (e) => {
          e.stopPropagation();
          GameState.ActionMenuManager.ActionPanels.selfPanels[i].nextAction();
          this.UpdateSelfUIIcon(i);
        });

      }

      this.BTN_ACTION0.setKeymapAction(KeyMapAction.PersonalPowerAct).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_FRIENDLY_POWER].Value
      );
      this.BTN_ACTION1.setKeymapAction(KeyMapAction.PersonalMedicalAct).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_FRIENDLY_ITEM].Value
      );
      this.BTN_ACTION2.setKeymapAction(KeyMapAction.PersonalOtherAct).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_FRIENDLY_NON_MEDICAL_ITEM].Value
      );
      this.BTN_ACTION3.setKeymapAction(KeyMapAction.PersonalMinesAct).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_FRIENDLY_MINE].Value
      );

      this.BTN_MINIMAP.addEventListener('click', (e) => {
        e.stopPropagation();
        this.manager.MenuMap.open();
      }).setKeymapAction(KeyMapAction.Map).setTooltipText(
        GameState.TLKManager.TLKStrings[TLK_TOOLTIP_MAP].Value
      );

      for(const texture of preloadTextures){
        const tex = await TextureLoader.Load(texture);
        preloadTexturesMap.set(texture, tex);
      }

      const arrowGeometry = new THREE.PlaneGeometry( 1, 1, 1 );
      const arrowMaterial = new THREE.MeshBasicMaterial( { color: 0xffffff, transparent: false, blending: THREE.AdditiveBlending } );
      arrowMaterial.map = preloadTexturesMap.get('friendlyarrow');
      this.namePlateArrowMaterial = arrowMaterial;
      this.namePlateArrow = new THREE.Mesh( arrowGeometry, arrowMaterial );
      this.namePlateArrow.scale.x = 32;
      this.namePlateArrow.scale.y = 32;
      this.namePlateArrow.position.z = 5;
      this.namePlateArrow.renderOrder = 5;
      this.namePlateArrow.visible = true;
      this.tGuiPanel.widget.add(this.namePlateArrow);

      // this.LBL_COMBATBG2.visible = false;
      resolve();
    });
  }

  showCombatUI() {
    this.BTN_CLEARALL?.show();
    this.BTN_CLEARONE?.show();
    this.LBL_COMBATBG1?.show();
    this.LBL_COMBATBG2?.show();
    this.LBL_COMBATBG3?.show();
    this.LBL_QUEUE0?.show();
    this.LBL_QUEUE1?.show();
    this.LBL_QUEUE2?.show();
    this.LBL_QUEUE3?.show();
  }

  hideCombatUI() {
    this.BTN_CLEARALL?.hide();
    this.BTN_CLEARONE?.hide();
    this.LBL_COMBATBG1?.hide();
    this.LBL_COMBATBG2?.hide();
    this.LBL_COMBATBG3?.hide();
    this.LBL_QUEUE0?.hide();
    this.LBL_QUEUE1?.hide();
    this.LBL_QUEUE2?.hide();
    this.LBL_QUEUE3?.hide();
    this.LBL_CMBTMSGBG?.hide();
    this.LBL_CMBTMODEMSG?.hide();
  }

  TogglePartyMember(nth = 0, bVisible = false) {
    if (!bVisible) { 
      this.getControlByName('LBL_CMBTEFCTRED' + (nth + 1))?.hide();
      this.getControlByName('LBL_CMBTEFCTINC' + (nth + 1))?.hide();
      this.getControlByName('LBL_LEVELUP' + (nth + 1))?.hide();
      this.getControlByName('LBL_LVLUPBG' + (nth + 1))?.hide();
      this.getControlByName('LBL_DEBILATATED' + (nth + 1))?.hide();
      this.getControlByName('LBL_DISABLE' + (nth + 1))?.hide();
      this.getControlByName('LBL_CHAR' + (nth + 1))?.hide();
      this.getControlByName('BTN_CHAR' + (nth + 1))?.hide();
      this.getControlByName('LBL_BACK' + (nth + 1))?.hide();
      this.getControlByName('PB_FORCE' + (nth + 1))?.hide();
      this.getControlByName('PB_VIT' + (nth + 1))?.hide();
    } else {
      this.getControlByName('LBL_CHAR' + (nth + 1))?.show();
      this.getControlByName('BTN_CHAR' + (nth + 1))?.show();
      this.getControlByName('LBL_BACK' + (nth + 1))?.show();
      this.getControlByName('PB_FORCE' + (nth + 1))?.show();
      this.getControlByName('PB_VIT' + (nth + 1))?.show();
      if (!GameState.module.area.miniGame && GameState.PartyManager.party[nth]) {
        switch (nth) {
        case 0:
          if (GameState.PartyManager.party[nth].canLevelUp()) {
            this.getControlByName('LBL_LEVELUP1').pulsing = true;
            this.getControlByName('LBL_LEVELUP1')?.show();
          } else {
            this.getControlByName('LBL_LEVELUP1')?.hide();
          }
          break;
        case 1:
          if (GameState.PartyManager.party[nth].canLevelUp()) {
            this.getControlByName('LBL_LEVELUP3').pulsing = true;
            this.getControlByName('LBL_LEVELUP3')?.show();
          } else {
            this.getControlByName('LBL_LEVELUP3')?.hide();
          }
          break;
        case 2:
          if (GameState.PartyManager.party[nth].canLevelUp()) {
            this.getControlByName('LBL_LEVELUP2').pulsing = true;
            this.getControlByName('LBL_LEVELUP2')?.show();
          } else {
            this.getControlByName('LBL_LEVELUP2')?.hide();
          }
          break;
        }
      }
    }
  }

  SetMapTexture(sTexture = '') {
    try {
      TextureLoader.Load(sTexture).then((texture: OdysseyTexture) => {
        this.miniMap.setTexture(texture);
      });
    } catch (e: any) {
      console.error(e);
    }
  }

  _canShowTargetUI() {
    if (BitWise.InstanceOfObject(GameState.CursorManager.selectedObject, ModuleObjectType.ModuleCreature) && GameState.CursorManager.selectedObject.isDead())
      return false;
    return (
      !this.manager.MenuContainer.bVisible && 
      GameState.CursorManager.reticle2.visible && 
      BitWise.InstanceOfObject(GameState.CursorManager.selectedObject, ModuleObjectType.ModuleObject) &&
      !BitWise.InstanceOfObject(GameState.CursorManager.selectedObject, ModuleObjectType.ModuleRoom)
    );
  }

  UpdateTargetUIIcon(index = 0) {
    const guiControl = this.getControlByName('LBL_TARGET' + index);
    if (!GameState.ActionMenuManager.ActionPanels.targetPanels[index].actions.length) {
      guiControl.setMaterialTexture(guiControl.border.fill.material, undefined);
      guiControl.setMaterialTexture(guiControl.highlight.fill.material, undefined);
      guiControl.setFillTextureName('');
      return;
    }

    const action = GameState.ActionMenuManager.ActionPanels.targetPanels[index].getSelectedAction();
    if (!action) {
      guiControl.setMaterialTexture(guiControl.border.fill.material, undefined);
      guiControl.setMaterialTexture(guiControl.highlight.fill.material, undefined);
      guiControl.setFillTextureName('');
      return;
    }

    if (guiControl.getFillTextureName() != action.icon) {
      guiControl.setFillTextureName(action.icon);
      guiControl.setHighlightFillTexture(action.icon);
      TextureLoader.tpcLoader.fetch(action.icon).then((texture: OdysseyTexture) => {
        guiControl.setMaterialTexture(guiControl.border.fill.material, texture);
        guiControl.setMaterialTexture(guiControl.highlight.fill.material, texture);
        guiControl.border.fill.material.transparent = true;
        guiControl.highlight.fill.material.transparent = true;
        guiControl.widget.position.z = 5;
      });
    }
  }

  UpdateSelfUIIcon(index = 0) {
    const guiControl = this.getControlByName('LBL_ACTION' + index);
    if (GameState.ActionMenuManager.ActionPanels.selfPanels[index].actions.length) {
      const action = GameState.ActionMenuManager.ActionPanels.selfPanels[index].getSelectedAction();
      if (action && guiControl.getFillTextureName() != action.icon) {
        guiControl.setFillTextureName(action.icon);
        guiControl.setHighlightFillTexture(action.icon);
        TextureLoader.tpcLoader.fetch(action.icon).then((texture: OdysseyTexture) => {
          guiControl.setMaterialTexture(guiControl.border.fill.material, texture);
          guiControl.setMaterialTexture(guiControl.highlight.fill.material, texture);
          guiControl.border.fill.material.transparent = true;
          guiControl.highlight.fill.material.transparent = true;
          guiControl.widget.position.z = 5;
        });
      } else if (!action) {
        guiControl.setMaterialTexture(guiControl.border.fill.material, undefined);
        guiControl.setMaterialTexture(guiControl.highlight.fill.material, undefined);
        guiControl.setFillTextureName('');
      }
    } else {
      guiControl.setMaterialTexture(guiControl.border.fill.material, undefined);
      guiControl.setMaterialTexture(guiControl.highlight.fill.material, undefined);
      guiControl.setFillTextureName('');
    }
  }

  targetScreenPosition: THREE.Vector3 = new THREE.Vector3();
  reticlePosition: THREE.Vector3 = new THREE.Vector3();
  reticleScreenPosition: THREE.Vector3 = new THREE.Vector3();
  updateArrow(selectedObject: ModuleObject) {
    this.namePlateArrow.visible = !!selectedObject;
    if(!selectedObject) return;
    
    const viewportXMin = -GameState.ResolutionManager.getViewportWidth()/2 + ARROW_SCALE_HALF;
    const viewportXMax = GameState.ResolutionManager.getViewportWidth()/2 - ARROW_SCALE_HALF;
    const viewportYMin2 = -GameState.ResolutionManager.getViewportHeight()/2 + ARROW_SCALE_HALF;
    const viewportYMax2 = GameState.ResolutionManager.getViewportHeight()/2 - ARROW_SCALE_HALF;
    const viewportYMin = -240/2 + ARROW_SCALE_HALF;
    const viewportYMax = 240/2 - ARROW_SCALE_HALF;
    const offLeft = this.reticleScreenPosition.x <= viewportXMin;
    const offRight = this.reticleScreenPosition.x >= viewportXMax;
    const offTop = this.reticleScreenPosition.y <= viewportYMin2;
    const offBottom = this.reticleScreenPosition.y >= viewportYMax2;
    const offScreen = offLeft || offRight || offTop || offBottom;

    const isOnScreen = GameState.viewportFrustum.containsPoint(this.reticlePosition);
    if(isOnScreen){
      this.namePlateArrow.visible = false;
      return;
    }

    this.namePlateArrow.position.x = Math.min(Math.max(this.reticleScreenPosition.x, viewportXMin), viewportXMax);
    this.namePlateArrow.position.y = -Math.min(Math.max(this.reticleScreenPosition.y, viewportYMin), viewportYMax);
    this.namePlateArrow.position.y += 50;
    if(offLeft){
      this.namePlateArrow.rotation.z = ARROW_DIR_LEFT;
    }else if(offRight){
      this.namePlateArrow.rotation.z = ARROW_DIR_RIGHT;
    }else{
      this.namePlateArrow.rotation.z = ARROW_DIR_DOWN;
    }
    this.namePlateArrow.scale.x = 32;
    this.namePlateArrow.scale.y = 32;
    this.namePlateArrow.position.z = 5;
    this.namePlateArrow.renderOrder = 5;
  }

  UpdateTargetUIPanels() {
    if (!this._canShowTargetUI()) {
      GameState.ActionMenuManager.SetTarget(undefined);
      this.LBL_NAME?.hide();
      this.LBL_NAMEBG?.hide();
      this.PB_HEALTH?.hide();
      this.LBL_HEALTHBG?.hide();
      for (let i = 0; i < 3; i++) {
        this.getControlByName('BTN_TARGET' + i)?.hide();
        this.getControlByName('LBL_TARGET' + i)?.hide();
        this.getControlByName('BTN_TARGETUP' + i)?.hide();
        this.getControlByName('BTN_TARGETDOWN' + i)?.hide();
      }
      this.updateArrow(undefined);
      return;
    }

    if (BitWise.InstanceOfObject(GameState.CursorManager.selectedObject, ModuleObjectType.ModuleCreature)) {
      if (GameState.CursorManager.selectedObject.isHostile(GameState.getCurrentPlayer()) && this.PB_HEALTH.getFillTextureName() == 'friend_bar') {
        this.PB_HEALTH.setFillTextureName('enemy_bar');
        this.PB_HEALTH.setFillTexture(preloadTexturesMap.get('enemy_bar'));
        this.namePlateArrowMaterial.map = preloadTexturesMap.get('hostilearrow');
      } else if (!GameState.CursorManager.selectedObject.isHostile(GameState.getCurrentPlayer()) && this.PB_HEALTH.getFillTextureName() == 'enemy_bar') {
        this.PB_HEALTH.setFillTextureName('friend_bar');
        this.PB_HEALTH.setFillTexture(preloadTexturesMap.get('friend_bar'));
        this.namePlateArrowMaterial.map = preloadTexturesMap.get('friendlyarrow');
      }
    } else {
      if (this.PB_HEALTH.getFillTextureName() != 'friend_bar') {
        this.PB_HEALTH.setFillTextureName('friend_bar');
        this.PB_HEALTH.setFillTexture(preloadTexturesMap.get('friend_bar'));
        this.namePlateArrowMaterial.map = preloadTexturesMap.get('friendlyarrow');
      }
    }
    if (this.manager.InGameOverlay.LBL_NAME.text.text != GameState.CursorManager.selectedObject.getName()) {
      this.LBL_NAME.setText(GameState.CursorManager.selectedObject.getName(), 25);
    }
    let health = 100 * Math.min(Math.max(GameState.CursorManager.selectedObject.getHP() / GameState.CursorManager.selectedObject.getMaxHP(), 0), 1);
    if (health > 100)
      health = 100;
    this.PB_HEALTH.setProgress(health);
    const maxBoundsX = GameState.ResolutionManager.getViewportWidth() / 2 + 640 / 2 - 125;
    const maxBoundsX2 = GameState.ResolutionManager.getViewportWidth() / 2 - 640 / 2 - 125;
    this.targetScreenPosition.set(640 / 2, 480 / 2, 0);
    if (BitWise.InstanceOfObject(GameState.CursorManager.selectedObject, ModuleObjectType.ModuleCreature)) {
      this.reticlePosition.copy(GameState.CursorManager.selectedObject.position);
      this.reticlePosition.z += 2;
    } else {
      this.reticlePosition.setFromMatrixPosition(GameState.CursorManager.reticle2.matrixWorld);
    }
    this.reticleScreenPosition.copy(this.reticlePosition);
    this.reticleScreenPosition.project(GameState.currentCamera);

    const widthHalf = GameState.ResolutionManager.getViewportWidth() / 2;
    const heightHalf = GameState.ResolutionManager.getViewportHeight() / 2;
    this.reticleScreenPosition.x = this.reticleScreenPosition.x * widthHalf;
    this.reticleScreenPosition.y = -(this.reticleScreenPosition.y * heightHalf);
    this.reticleScreenPosition.z = 0;
    this.updateArrow(GameState.CursorManager.selectedObject);
    
    this.targetScreenPosition.add(this.reticleScreenPosition);
    if (this.targetScreenPosition.x > maxBoundsX) {
      this.targetScreenPosition.x = maxBoundsX;
    }
    if (this.targetScreenPosition.x < -maxBoundsX2) {
      this.targetScreenPosition.x = -maxBoundsX2;
    }
    if (this.targetScreenPosition.y > 640 / 2) {
      this.targetScreenPosition.y = 640 / 2;
    }
    if (this.targetScreenPosition.y < 100) {
      this.targetScreenPosition.y = 100;
    }
    this.LBL_NAME.scale = this.LBL_NAMEBG.scale = this.PB_HEALTH.scale = this.LBL_HEALTHBG.scale = false;
    this.LBL_NAME?.show();
    this.LBL_NAMEBG?.show();
    this.PB_HEALTH?.show();
    this.LBL_HEALTHBG?.show();
    this.LBL_NAME.extent.left = this.targetScreenPosition.x - 20;
    this.LBL_NAME.anchor = Anchor.User;
    this.LBL_NAMEBG.extent.left = this.targetScreenPosition.x - 20;
    this.LBL_NAMEBG.anchor = Anchor.User;
    this.PB_HEALTH.extent.left = this.targetScreenPosition.x - 20;
    this.PB_HEALTH.anchor = Anchor.User;
    this.LBL_HEALTHBG.extent.left = this.targetScreenPosition.x - 20;
    this.LBL_HEALTHBG.anchor = Anchor.User;
    this.LBL_NAME.extent.top = this.targetScreenPosition.y - 38;
    this.LBL_NAMEBG.extent.top = this.targetScreenPosition.y - 38;
    this.PB_HEALTH.extent.top = this.targetScreenPosition.y - 12;
    this.LBL_HEALTHBG.extent.top = this.targetScreenPosition.y - 12;
    this.LBL_NAME.recalculate();
    this.LBL_NAMEBG.recalculate();
    this.PB_HEALTH.recalculate();
    this.LBL_HEALTHBG.recalculate();
    if (!!GameState.ActionMenuManager.targetActionCount()) {
      for (let i = 0; i < GameState.ActionMenuManager.TARGET_MENU_COUNT; i++) {
        let xPos = (this.getControlByName('BTN_TARGET' + i).extent.width + 5) * i + 20;
        this.getControlByName('BTN_TARGET' + i).scale = false;
        this.getControlByName('BTN_TARGET' + i).extent.left = this.targetScreenPosition.x + xPos;
        this.getControlByName('BTN_TARGET' + i).extent.top = this.targetScreenPosition.y;
        this.getControlByName('BTN_TARGET' + i).anchor = Anchor.User;
        this.getControlByName('LBL_TARGET' + i).scale = false;
        this.getControlByName('LBL_TARGET' + i).extent.left = this.targetScreenPosition.x + xPos + 3;
        this.getControlByName('LBL_TARGET' + i).extent.top = this.targetScreenPosition.y + 14;
        this.getControlByName('LBL_TARGET' + i).anchor = Anchor.User;
        this.getControlByName('BTN_TARGETUP' + i).scale = false;
        this.getControlByName('BTN_TARGETUP' + i).extent.left = this.targetScreenPosition.x + xPos;
        this.getControlByName('BTN_TARGETUP' + i).extent.top = this.targetScreenPosition.y + 5;
        this.getControlByName('BTN_TARGETUP' + i).anchor = Anchor.User;
        this.getControlByName('BTN_TARGETDOWN' + i).scale = false;
        this.getControlByName('BTN_TARGETDOWN' + i).extent.left = this.targetScreenPosition.x + xPos;
        this.getControlByName('BTN_TARGETDOWN' + i).extent.top = this.targetScreenPosition.y + (this.getControlByName('BTN_TARGET' + i).extent.height / 2 + 12);
        this.getControlByName('BTN_TARGETDOWN' + i).widget.rotation.z = Math.PI;
        this.getControlByName('BTN_TARGETDOWN' + i).anchor = Anchor.User;
        this.UpdateTargetUIIcon(i);
        this.getControlByName('BTN_TARGETUP' + i).recalculate();
        this.getControlByName('BTN_TARGETDOWN' + i).recalculate();
        this.getControlByName('BTN_TARGET' + i).recalculate();
        this.getControlByName('LBL_TARGET' + i).recalculate();
        this.getControlByName('BTN_TARGET' + i)?.show();
        this.getControlByName('LBL_TARGET' + i)?.show();
        
        if(GameState.ActionMenuManager.ActionPanels.targetPanels[i].actions.length <= 1){
          this.getControlByName('BTN_TARGETUP' + i)?.hide();
          this.getControlByName('BTN_TARGETDOWN' + i)?.hide();
        }else{
          this.getControlByName('BTN_TARGETUP' + i)?.show();
          this.getControlByName('BTN_TARGETDOWN' + i)?.show();
        }
      }
    } else {
      for (let i = 0; i < 3; i++) {
        this.getControlByName('BTN_TARGET' + i)?.hide();
        this.getControlByName('LBL_TARGET' + i)?.hide();
        this.getControlByName('BTN_TARGETUP' + i)?.hide();
        this.getControlByName('BTN_TARGETDOWN' + i)?.hide();
      }
    }
  }

  UpdateSelfUIPanels(delta = 0) {
    for (let i = 0; i < GameState.ActionMenuManager.SELF_MENU_COUNT; i++) {
      this.UpdateSelfUIIcon(i);
      this.getControlByName('BTN_ACTIONUP' + i).recalculate();
      this.getControlByName('BTN_ACTIONDOWN' + i).recalculate();
        
      if(GameState.ActionMenuManager.ActionPanels.selfPanels[i].actions.length <= 1){
        this.getControlByName('BTN_ACTIONUP' + i)?.hide();
        this.getControlByName('BTN_ACTIONDOWN' + i)?.hide();
      }else{
        this.getControlByName('BTN_ACTIONUP' + i)?.show();
        this.getControlByName('BTN_ACTIONDOWN' + i)?.show();
      }
    }
  }

  /**
   * Update HUD Notification Icons
   * 
   * @param delta 
   */
  UpdateHUDNotificationIcons(delta = 0){
    if(GameState.UINotificationManager.bNotificationSoundQueued){
      GameState.UINotificationManager.bNotificationSoundQueued = false;
      GameState.guiAudioEmitter.playSoundFireAndForget('gui_quest');
    }
    /**
     * Item Lost
     */
    if(GameState.UINotificationManager.ItemLostTimer > 0){
      this.LBL_ITEMLOST.show();
      this.LBL_ITEMLOST.pulsing = true;
      GameState.UINotificationManager.ItemLostTimer -= delta;
    }else{
      GameState.UINotificationManager.ItemLostTimer = 0;
      this.LBL_ITEMLOST.hide();
    }

    /**
     * Item Received
     */
    if(GameState.UINotificationManager.ItemReceivedTimer > 0){
      this.LBL_ITEMRCVD.show();
      this.LBL_ITEMRCVD.pulsing = true;
      GameState.UINotificationManager.ItemReceivedTimer -= delta;
    }else{
      GameState.UINotificationManager.ItemReceivedTimer = 0;
      this.LBL_ITEMRCVD.hide();
    }

    /**
     * Credits Received
     */
    if(GameState.UINotificationManager.CreditsReceivedTimer > 0){
      this.LBL_CASH.show();   
      this.LBL_CASH.pulsing = true;
      GameState.UINotificationManager.CreditsReceivedTimer -= delta;
    }else{
      GameState.UINotificationManager.CreditsReceivedTimer = 0;
      this.LBL_CASH.hide();
    }

    /**
     * Journal Entry Added
     */
    if(GameState.UINotificationManager.JournalEntryAddedTimer > 0){
      this.LBL_JOURNAL.show();
      this.LBL_JOURNAL.pulsing = true;
      GameState.UINotificationManager.JournalEntryAddedTimer -= delta;
    }else{
      GameState.UINotificationManager.JournalEntryAddedTimer = 0;
      this.LBL_JOURNAL.hide();
    }

    /**
     * Light Shift
     */
    if(GameState.UINotificationManager.LightShiftTimer > 0){
      this.LBL_LIGHTSHIFT.show();
      this.LBL_LIGHTSHIFT.pulsing = true;
      GameState.UINotificationManager.LightShiftTimer -= delta;
    }else{
      GameState.UINotificationManager.LightShiftTimer = 0;
      this.LBL_LIGHTSHIFT.hide();
    }

    /**
     * Dark Shift
     */
    if(GameState.UINotificationManager.DarkShiftTimer > 0){
      this.LBL_DARKSHIFT.show();
      this.LBL_DARKSHIFT.pulsing = true;
      GameState.UINotificationManager.DarkShiftTimer -= delta;
    }else{
      GameState.UINotificationManager.DarkShiftTimer = 0;
      this.LBL_DARKSHIFT.hide();
    }

    /**
     * Plot XP Received
     */
    if(GameState.UINotificationManager.PlotXPReceivedTimer > 0){
      this.LBL_PLOTXP.show();
      this.LBL_PLOTXP.pulsing = true;
      GameState.UINotificationManager.PlotXPReceivedTimer -= delta;
    }else{
      GameState.UINotificationManager.PlotXPReceivedTimer = 0;
      this.LBL_PLOTXP.hide();
    }

    /**
     * Stealth XP Received
     */
    if(GameState.UINotificationManager.StealthXPReceivedTimer > 0){
      this.LBL_STEALTHXP.show();
      this.LBL_STEALTHXP.pulsing = true;
      GameState.UINotificationManager.StealthXPReceivedTimer -= delta;
    }else{
      GameState.UINotificationManager.StealthXPReceivedTimer = 0;
      this.LBL_STEALTHXP.hide();
    }
  }

  update(delta = 0) {
    super.update(delta);
    if (!this.bVisible)
      return;

    if (GameState.module.area.miniGame) { return; }

    const oPC = GameState.getCurrentPlayer();
    GameState.ActionMenuManager.SetPC(oPC);
    GameState.ActionMenuManager.SetTarget(GameState.CursorManager.selectedObject);
    GameState.ActionMenuManager.UpdateMenuActions();
    this.UpdateTargetUIPanels();
    this.UpdateSelfUIPanels();
    this.UpdateHUDNotificationIcons(delta);

    //update minimap
    this.miniMap.setPosition(oPC.position.x, oPC.position.y);
    this.miniMap.setRotation(GameState.controls.camera.rotation.z);
    this.miniMap.render(delta);

    this.TogglePartyMember(0, false);
    this.TogglePartyMember(1, false);
    this.TogglePartyMember(2, false);
    
    for (let i = 0; i < GameState.PartyManager.party.length; i++) {
      let partyMember = GameState.PartyManager.party[i];
      let id = i;
      switch (i) {
      case 1:
        id = 2;
        break;
      case 2:
        id = 1;
        break;
      }
      this.TogglePartyMember(id, true);
      let pmBG = this.getControlByName('LBL_CHAR' + (id + 1));
      const portraitResRef = partyMember.getPortraitResRef();
      if (pmBG.getFillTextureName() != portraitResRef) {
        pmBG.setFillTextureName(portraitResRef);
        TextureLoader.tpcLoader.fetch(portraitResRef).then((texture: OdysseyTexture) => {
          pmBG.setFillTexture(texture);
        });
      }
      (this.getControlByName('PB_VIT' + (id + 1)) as GUIProgressBar).setProgress(Math.min(Math.max(partyMember.getHP() / partyMember.getMaxHP(), 0), 1) * 100);
      (this.getControlByName('PB_FORCE' + (id + 1)) as GUIProgressBar).setProgress(Math.min(Math.max(partyMember.getFP() / partyMember.getMaxFP(), 0), 1) * 100);
      if (partyMember.isDebilitated()) {
        this.getControlByName('LBL_DEBILATATED' + (id + 1))?.show();
      } else {
        this.getControlByName('LBL_DEBILATATED' + (id + 1))?.hide();
      }
    }
    if (oPC.excitedDuration || oPC.combatRound.scheduledActionList.length) {
      this.showCombatUI();
      let action0 = oPC.combatRound.action;
      let action1 = oPC.combatRound.scheduledActionList[0];
      let action2 = oPC.combatRound.scheduledActionList[1];
      let action3 = oPC.combatRound.scheduledActionList[2];
      if (action0 != undefined) {
        if (this.LBL_QUEUE0.getFillTextureName() != action0.iconResRef) {
          this.LBL_QUEUE0.setFillTextureName(action0.iconResRef);
          TextureLoader.tpcLoader.fetch(action0.iconResRef).then((texture: OdysseyTexture) => {
            this.LBL_QUEUE0.setFillTexture(texture);
            this.LBL_QUEUE0.border.fill.material.transparent = true;
          });
        }
      } else {
        this.LBL_QUEUE0.setFillTextureName('');
        this.LBL_QUEUE0.setFillTexture(undefined);
      }
      if (action1 != undefined) {
        if (this.LBL_QUEUE1.getFillTextureName() != action1.iconResRef) {
          this.LBL_QUEUE1.setFillTextureName(action1.iconResRef);
          TextureLoader.tpcLoader.fetch(action1.iconResRef).then((texture: OdysseyTexture) => {
            this.LBL_QUEUE1.setFillTexture(texture);
            this.LBL_QUEUE1.border.fill.material.transparent = true;
          });
        }
      } else {
        this.LBL_QUEUE1.setFillTextureName('');
        this.LBL_QUEUE1.setFillTexture(undefined);
      }
      if (action2 != undefined) {
        if (this.LBL_QUEUE2.getFillTextureName() != action2.iconResRef) {
          this.LBL_QUEUE2.setFillTextureName(action2.iconResRef);
          TextureLoader.tpcLoader.fetch(action2.iconResRef).then((texture: OdysseyTexture) => {
            this.LBL_QUEUE2.setFillTexture(texture);
            this.LBL_QUEUE2.border.fill.material.transparent = true;
          });
        }
      } else {
        this.LBL_QUEUE2.setFillTextureName('');
        this.LBL_QUEUE2.setFillTexture(undefined);
      }
      if (action3 != undefined) {
        if (this.LBL_QUEUE3.getFillTextureName() != action3.iconResRef) {
          this.LBL_QUEUE3.setFillTextureName(action3.iconResRef);
          TextureLoader.tpcLoader.fetch(action3.iconResRef).then((texture: OdysseyTexture) => {
            this.LBL_QUEUE3.setFillTexture(texture);
            this.LBL_QUEUE3.border.fill.material.transparent = true;
          });
        }
      } else {
        this.LBL_QUEUE3.setFillTextureName('');
        this.LBL_QUEUE3.setFillTexture(undefined);
      }
    } else {
      this.hideCombatUI();
    }
  }

  show() {
    super.show();
    this.BTN_ACTIONDOWN0.flipY();
    this.BTN_ACTIONDOWN1.flipY();
    this.BTN_ACTIONDOWN2.flipY();
    this.BTN_ACTIONDOWN3.flipY();
  }

  resize() {
    this.recalculatePosition();
  }

  triggerControllerAPress() {
    if (GameState.CursorManager.selectedObject) {
      if (typeof GameState.CursorManager.selectedObject.onClick === 'function') {
        GameState.getCurrentPlayer().clearAllActions();
        GameState.CursorManager.selectedObject.onClick(GameState.getCurrentPlayer());
      } else {
        let distance = GameState.getCurrentPlayer().position.distanceTo(GameState.CursorManager.selectedObject.position);
        if (distance > 1.5) {
          GameState.getCurrentPlayer().clearAllActions();
          GameState.CursorManager.selectedObject.clearAllActions();
          GameState.getCurrentPlayer().actionDialogObject(GameState.CursorManager.selectedObject);
        }
      }
    }
  }
  
}
